import React, { useEffect, useMemo, useState } from 'react';
import ReactSwitch from 'react-switch';
import { cloneDeep } from 'lodash';
import { Button, Input } from 'src/components';
import { useAlerts } from 'src/common/AlertManager';
import { useUpdateAdminUserSettingsMutation } from 'src/api/mutations/adminUserSettingsMutations';

import styles from '../styles.module.scss';

interface DatabaseTabProps {
  userId: number;
  userSettings: any;
}

const DatabaseTab: React.FC<DatabaseTabProps> = ({ userId, userSettings }) => {
  const { showSuccess, showAlert } = useAlerts();
  const updateSettingsMutation = useUpdateAdminUserSettingsMutation(userId);

  const serverDatabase = userSettings?.database;
  const [local, setLocal] = useState<any>(() => cloneDeep(serverDatabase) || {});

  useEffect(() => {
    if (serverDatabase) setLocal(cloneDeep(serverDatabase));
  }, [serverDatabase]);

  const hasChanges = useMemo(
    () => JSON.stringify(local) !== JSON.stringify(serverDatabase),
    [local, serverDatabase],
  );

  const events = useMemo(() => {
    if (!local.db_export_events) return [];
    return Object.entries(local.db_export_events).map(([key, value]: [string, any]) => ({
      key,
      enabled: value,
    }));
  }, [local.db_export_events]);

  const handleSave = async () => {
    try {
      await updateSettingsMutation.mutateAsync({ key: 'database', value: local });
      showSuccess('Database settings saved');
    } catch {
      showAlert('Failed to save database settings');
    }
  };

  if (!serverDatabase) {
    return <div className={styles.emptyState}>No database settings configured for this user.</div>;
  }

  return (
    <div>
      <h4 className={styles.settingsSectionTitle}>Database</h4>
      <div className={styles.inputSection}>
        <Input
          variant="secondary"
          value={local.db_url}
          onChange={(value: string) => setLocal((prev: any) => ({ ...prev, db_url: value }))}
          placeholder=""
        />
        <p className={styles.sectionDescription}>
          Database URL for data export. Only MySQL database is supported.
        </p>
      </div>

      <div className={styles.webhookOptions}>
        <h4 className={styles.webhookOptionsTitle}>Asset events</h4>
        <p className={styles.sectionDescription}>For Database Export, all events are enabled by default</p>
        {events.map(({ key }) => (
          <div key={key} className={styles.webhookOption}>
            <ReactSwitch
              checked={true}
              onChange={() => {}}
              checkedIcon={false}
              uncheckedIcon={false}
              onColor="#2e53e3"
              disabled
            />
            <label>{key}</label>
          </div>
        ))}
      </div>

      <div className={styles.settingsActions}>
        <Button
          mode="primary"
          size="small"
          onClick={handleSave}
          disabled={!hasChanges || updateSettingsMutation.isLoading}
        >
          Save Database Settings
        </Button>
      </div>
    </div>
  );
};

export default DatabaseTab;
